import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SplashScreen } from "@capacitor/splash-screen";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import ChatWidget from "@/components/ChatWidget";
import OnboardingTour from "@/components/OnboardingTour";
import ErrorBoundary from "@/components/ErrorBoundary";
import { isNative } from "@/lib/platform";
import { useEffect } from "react";
import { initPushNotifications } from "@/lib/push";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Devices from "@/pages/Devices";
import Subscription from "@/pages/Subscription";
import Claims from "@/pages/Claims";
import ClaimNew from "@/pages/ClaimNew";
import ClaimDetail from "@/pages/ClaimDetail";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import B2B from "@/pages/B2B";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicClaim from "@/pages/PublicClaim";
import Referrals from "@/pages/Referrals";
import Masters from "@/pages/Masters";
import CrmShell from "@/pages/crm/CrmShell";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import CrmIndex from "@/pages/crm/CrmIndex";
import Leads from "@/pages/crm/Leads";
import Deals from "@/pages/crm/Deals";
import Tasks from "@/pages/crm/Tasks";
import TaskDetail from "@/pages/crm/TaskDetail";
import Campaigns from "@/pages/crm/Campaigns";
import Inbox from "@/pages/crm/Inbox";
import Tickets from "@/pages/crm/Tickets";
import TicketDetail from "@/pages/crm/TicketDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import { PostList, PostDetail } from "@/pages/Posts";
import AdminContent from "@/pages/AdminContent";
import AdminSeoEngine from "@/pages/AdminSeoEngine";
import AdminDripSequences from "@/pages/AdminDripSequences";
import AdminMarketingDashboards from "@/pages/AdminMarketingDashboards";
import AdminAccounting from "@/pages/AdminAccounting";
import { SeoHub, SeoPage } from "@/pages/SeoPage";
import AcceptInvite from "@/pages/AcceptInvite";
import Employees from "@/pages/crm/Employees";
import Nurture from "@/pages/crm/Nurture";
import Proposals from "@/pages/crm/Proposals";
import Handoffs from "@/pages/crm/Handoffs";
import Orgs from "@/pages/crm/Orgs";
import Referrers from "@/pages/crm/Referrers";
import AgentPortal from "@/pages/AgentPortal";
import Marketing from "@/pages/crm/Marketing";
import MarketingLists from "@/pages/crm/MarketingLists";
import MarketingBroadcasts from "@/pages/crm/MarketingBroadcasts";
import MarketingTemplates from "@/pages/crm/MarketingTemplates";
import MailInLog from "@/pages/crm/MailInLog";
import Unsubscribe from "@/pages/Unsubscribe";
import RepairCostEstimator from "@/pages/RepairCostEstimator";
import ComparePage from "@/pages/ComparePage";
import Gems from "@/pages/Gems";
import AdminRaffle from "@/pages/AdminRaffle";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminLoaners from "@/pages/AdminLoaners";
import Escalation from "@/pages/crm/Escalation";
import LegalPage from "@/pages/LegalPage";
import DeleteAccount from "@/pages/DeleteAccount";
import Wallet from "@/pages/Wallet";
import AgentPaymentLinks from "@/pages/AgentPaymentLinks";
import PayLink from "@/pages/PayLink";
import AdminWallets from "@/pages/AdminWallets";
import Wrapped from "@/pages/Wrapped";
import Challenges from "@/pages/Challenges";
import Gifts from "@/pages/Gifts";
import Impact from "@/pages/Impact";
import HealthScore from "@/pages/HealthScore";
import InstantQuote from "@/pages/InstantQuote";
import AdminEnhancements from "@/pages/AdminEnhancements";
import Collection from "@/pages/Collection";

function AppShell({ children }) {
  const { user } = useAuth();
  const native = isNative();
  useEffect(() => {
    if (user) initPushNotifications().catch(() => {});
  }, [user]);
  return (
    <AppLayout>
      <ErrorBoundary>{children}</ErrorBoundary>
      {user && !native && <ChatWidget />}
      {user?.role === "customer" && !native && <OnboardingTour userId={user.id} />}
    </AppLayout>
  );
}

function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  // Tag body for native — used by CSS to disable backdrop-filter/blur/grain
  // effects that crash the Android WebView renderer.
  useEffect(() => {
    if (isNative()) {
      document.body.classList.add("native");
      // Safety net: iPadOS 26.6 sometimes ignores Capacitor's launchAutoHide,
      // leaving the splash on-screen and blocking every tap. Force-hide it
      // as soon as JS bootstraps. Ignore errors on web.
      SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {});
      const t = setTimeout(() => SplashScreen.hide().catch(() => {}), 1500);
      return () => clearTimeout(t);
    }
  }, []);
  return (
    <div className="App font-sans">
      <GoogleOAuthProvider clientId={googleClientId}>
        <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors duration={3500} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support" element={<Navigate to="/contact" replace />} />
            <Route path="/help" element={<Navigate to="/contact" replace />} />
            <Route path="/blog" element={<PostList category="blog" />} />
            <Route path="/blog/:slug" element={<PostDetail category="blog" />} />
            <Route path="/news" element={<PostList category="news" />} />
            <Route path="/news/:slug" element={<PostDetail category="news" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim/:token" element={<PublicClaim />} />
            <Route path="/repair-cost" element={<RepairCostEstimator />} />
            <Route path="/quote" element={<InstantQuote />} />
            <Route path="/vs-applecare" element={<ComparePage provider="applecare" />} />
            <Route path="/vs-samsungcare" element={<ComparePage provider="samsungcare" />} />
            {/* SEO Content Engine — hub + dynamic slug pages per category */}
            <Route path="/cost" element={<SeoHub category="cost_guide" />} />
            <Route path="/cost/:slug" element={<SeoPage category="cost_guide" />} />
            <Route path="/compare" element={<SeoHub category="comparison" />} />
            <Route path="/compare/:slug" element={<SeoPage category="comparison" />} />
            <Route path="/repair" element={<SeoHub category="location" />} />
            <Route path="/repair/:slug" element={<SeoPage category="location" />} />
            <Route path="/guide" element={<SeoHub category="blog" />} />
            <Route path="/guide/:slug" element={<SeoPage category="blog" />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/collection" element={<Collection />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/pay/:code" element={<PayLink />} />
            <Route path="/pay/:code/success" element={<PayLink />} />
            <Route path="/checkout/success" element={
              <ProtectedRoute><CheckoutSuccess /></ProtectedRoute>
            } />
            <Route path="/app" element={<ProtectedRoute><AppShell><Dashboard /></AppShell></ProtectedRoute>} />
            <Route path="/app/devices" element={<ProtectedRoute><AppShell><Devices /></AppShell></ProtectedRoute>} />
            <Route path="/app/subscription" element={<ProtectedRoute><AppShell><Subscription /></AppShell></ProtectedRoute>} />
            <Route path="/app/claims" element={<ProtectedRoute><AppShell><Claims /></AppShell></ProtectedRoute>} />
            <Route path="/app/claims/new" element={<ProtectedRoute><AppShell><ClaimNew /></AppShell></ProtectedRoute>} />
            <Route path="/app/claims/:id" element={<ProtectedRoute><AppShell><ClaimDetail /></AppShell></ProtectedRoute>} />
            <Route path="/app/notifications" element={<ProtectedRoute><AppShell><Notifications /></AppShell></ProtectedRoute>} />
            <Route path="/app/referrals" element={<ProtectedRoute><AppShell><Referrals /></AppShell></ProtectedRoute>} />
            <Route path="/app/gems" element={<ProtectedRoute><AppShell><Gems /></AppShell></ProtectedRoute>} />
            <Route path="/app/wallet" element={<ProtectedRoute><AppShell><Wallet /></AppShell></ProtectedRoute>} />
            <Route path="/app/wrapped" element={<ProtectedRoute><AppShell><Wrapped /></AppShell></ProtectedRoute>} />
            <Route path="/app/challenges" element={<ProtectedRoute><AppShell><Challenges /></AppShell></ProtectedRoute>} />
            <Route path="/app/gifts" element={<ProtectedRoute><AppShell><Gifts /></AppShell></ProtectedRoute>} />
            <Route path="/app/impact" element={<ProtectedRoute><AppShell><Impact /></AppShell></ProtectedRoute>} />
            <Route path="/app/health-score" element={<ProtectedRoute><AppShell><HealthScore /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/enhancements" element={<ProtectedRoute roles={["admin"]}><AppShell><AdminEnhancements /></AppShell></ProtectedRoute>} />
            <Route path="/app/agent/payment-links" element={<ProtectedRoute roles={["admin","b2b_agent","b2b_admin","support"]}><AppShell><AgentPaymentLinks /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin" element={<ProtectedRoute roles={["admin"]}><AppShell><Admin /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/analytics" element={<ProtectedRoute roles={["admin"]}><AppShell><AdminAnalytics /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/raffle" element={<ProtectedRoute roles={["admin"]}><AppShell><AdminRaffle /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/loaners" element={<ProtectedRoute roles={["admin","logistics","support"]}><AppShell><AdminLoaners /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/wallets" element={<ProtectedRoute roles={["admin"]}><AppShell><AdminWallets /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/masters" element={<ProtectedRoute roles={["admin"]}><AppShell><Masters /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/content" element={<ProtectedRoute roles={["admin","marketing"]}><AppShell><AdminContent /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/seo" element={<ProtectedRoute roles={["admin","marketing"]}><AppShell><AdminSeoEngine /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/drips" element={<ProtectedRoute roles={["admin","marketing"]}><AppShell><AdminDripSequences /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/marketing" element={<ProtectedRoute roles={["admin","marketing"]}><AppShell><AdminMarketingDashboards /></AppShell></ProtectedRoute>} />
            <Route path="/app/admin/accounting" element={<ProtectedRoute roles={["admin","finance"]}><AppShell><AdminAccounting /></AppShell></ProtectedRoute>} />
            <Route path="/app/crm" element={<ProtectedRoute roles={["admin","support","sales","marketing","logistics","b2b_admin","customer"]}><AppShell><CrmShell /></AppShell></ProtectedRoute>}>
              <Route index element={<CrmIndex />} />
              <Route path="dashboard" element={<CrmDashboard />} />
              <Route path="leads" element={<Leads />} />
              <Route path="deals" element={<Deals />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/:id" element={<TaskDetail />} />
              <Route path="employees" element={<Employees />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="tickets/:id" element={<TicketDetail />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="nurture" element={<Nurture />} />
              <Route path="proposals" element={<Proposals />} />
              <Route path="handoffs" element={<Handoffs />} />
              <Route path="orgs" element={<Orgs />} />
              <Route path="referrers" element={<Referrers />} />
              <Route path="mail-in-log" element={<MailInLog />} />
              <Route path="escalation" element={<Escalation />} />
              <Route path="marketing" element={<Marketing />}>
                <Route index element={<MarketingLists />} />
                <Route path="broadcasts" element={<MarketingBroadcasts />} />
                <Route path="templates" element={<MarketingTemplates />} />
              </Route>
            </Route>
            <Route path="/app/agent" element={<ProtectedRoute><AppShell><AgentPortal /></AppShell></ProtectedRoute>} />
            <Route path="/app/b2b" element={<ProtectedRoute roles={["b2b_admin","b2b_agent","admin"]}><AppShell><B2B /></AppShell></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
